import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  MenuItem,
  Alert,
  Chip,
  Autocomplete
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { laptopAPI } from '../services/api';

const LaptopForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    deviceName: '',
    serialNumber: '',
    manufacturer: '',
    model: '',
    processor: '',
    ram: '',
    storage: '',
    os: '',
    skuFamily: '',
    location: '',
    primaryUserEmail: '',
    primaryUserDisplayName: '',
    status: 'ready-to-use',
    purchaseDate: '',
    warrantyExpiry: '',
    notes: '',
    tags: []
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadLaptop();
    }
  }, [id]);

  const loadLaptop = async () => {
    try {
      const response = await laptopAPI.getById(id);
      if (response.data.success) {
        const laptop = response.data.data;
        setFormData({
          deviceName: laptop.deviceName || '',
          serialNumber: laptop.serialNumber || '',
          manufacturer: laptop.manufacturer || '',
          model: laptop.model || '',
          processor: laptop.processor || '',
          ram: laptop.ram || '',
          storage: laptop.storage || '',
          os: laptop.os || '',
          skuFamily: laptop.skuFamily || '',
          location: laptop.location || '',
          primaryUserEmail: laptop.primaryUserEmail || '',
          primaryUserDisplayName: laptop.primaryUserDisplayName || '',
          status: laptop.status || 'ready-to-use',
          purchaseDate: laptop.purchaseDate ? laptop.purchaseDate.split('T')[0] : '',
          warrantyExpiry: laptop.warrantyExpiry ? laptop.warrantyExpiry.split('T')[0] : '',
          notes: laptop.notes || '',
          tags: laptop.tags || []
        });
      }
    } catch (error) {
      setError('Failed to load laptop details');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit) {
        await laptopAPI.update(id, formData);
      } else {
        await laptopAPI.create(formData);
      }
      navigate('/laptops');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save laptop');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/laptops')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div">
            {isEdit ? 'Edit Laptop' : 'Add New Laptop'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Device Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Device Name"
                  name="deviceName"
                  value={formData.deviceName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Serial Number"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Manufacturer"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Model"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Specifications
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Processor"
                  name="processor"
                  value={formData.processor}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RAM"
                  name="ram"
                  placeholder="e.g., 16GB"
                  value={formData.ram}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Storage"
                  name="storage"
                  placeholder="e.g., 512GB SSD"
                  value={formData.storage}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Operating System"
                  name="os"
                  value={formData.os}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SKU Family"
                  name="skuFamily"
                  value={formData.skuFamily}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Assignment Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="ready-to-use">Ready to Use</MenuItem>
                  <MenuItem value="in-repair">In Repair</MenuItem>
                  <MenuItem value="not-ready-to-use">Not Ready to Use</MenuItem>
                  <MenuItem value="no-use">No Use</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary User Email"
                  name="primaryUserEmail"
                  type="email"
                  value={formData.primaryUserEmail}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary User Display Name"
                  name="primaryUserDisplayName"
                  value={formData.primaryUserDisplayName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchaseDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.purchaseDate}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Warranty Expiry"
                  name="warrantyExpiry"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.warrantyExpiry}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={formData.tags}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      tags: newValue
                    }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Type and press Enter to add tags"
                      helperText="Add tags to categorize this laptop (e.g., 'developer', 'finance', 'loaner')"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/laptops')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : isEdit ? 'Update Laptop' : 'Create Laptop'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default LaptopForm;
